import {addMonths, isWithinInterval, subMonths} from 'date-fns';

import {isNotEmpty, isNotNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {datagridApi} from '@dms/api/core';
import {DataQueryOptions, useLazyDataQuery} from '@dms/shared';

import {useQueryState, parseDate, getApiDateString, useOnMount} from 'shared/web';

export function useRentalOrderCalendarControl() {
  const [selectedDate, setSelectedDate] = useQueryState(SELECTED_DATE);
  const [dueDateFrom, setDueDateFrom] = useQueryState(DUE_DATE_FROM);
  const [dueDateTo, setDueDateTo] = useQueryState(DUE_DATE_TO);

  const parsedSelectedDate =
    isNotNil(selectedDate) && isNotEmpty(selectedDate) ? parseDate(selectedDate) : new Date();

  const [getLazyDataQuery, {data: dataQuery, isError: isGridQueryErrored}] =
    useLazyDataQuery('rental-order');

  const {data: rentalOrders, isError: isGridErrored} = datagridApi.useGetDataByDataQueryQuery(
    {
      gridCode: 'rental-order',
      dataQueryId: dataQuery?.dataQueryId!,
      offset: 0,
      limit: 100000,
      body: {
        columns: [
          'scheduledRent',
          'scheduledReturn',
          'number',
          'dailyRate',
          'expectedPriceWithVat',
          'salePriceWithVat',
          'expectedPrice',
          'salePrice',
          'licensePlate',
          'rentalLength',
          'customer',
          'vehicleTitle',
          'vin',
          'state',
          'vehicleRent',
          'vehicleReturn',
          'description',
          'author',
          'rentType',
          'delayed',
          'address',
          'returnAddress',
          'serviceOrder',
          'rentMileage',
          'returnMileage',
          'branch',
          'owner',
          'assignee',
          'createdAt',
          'updatedAt',
        ],
      },
    },
    {
      skip: isNilOrEmpty(dataQuery?.dataQueryId),
      refetchOnMountOrArgChange: true,
      pollingInterval: TASK_POLLING_INTERVAL,
    }
  );

  const onFilterChange = (date: Date) => {
    const dateFrom = subMonths(date, 3);
    const dateTo = addMonths(date, 3);

    const args: DataQueryOptions = {
      filters: {
        scheduledRent: {
          from: getApiDateString(dateFrom),
          to: getApiDateString(dateTo),
        },
      },
    };

    getLazyDataQuery(args);
    setDueDateFrom(getApiDateString(dateFrom));
    setDueDateTo(getApiDateString(dateTo));
    setSelectedDate(getApiDateString(date));
  };

  useOnMount(() => {
    onFilterChange(parsedSelectedDate);
  });

  /**
   * Handles calendar date navigation
   * Updates the current date in URL query params and triggers the date change callback
   * @param {Date} date - New selected date
   */
  const onNavigate = (date: Date) => {
    if (
      isNotNil(dueDateFrom) &&
      isNotEmpty(dueDateFrom) &&
      isNotNil(dueDateTo) &&
      isNotEmpty(dueDateTo)
    ) {
      const isDateInRange = isWithinInterval(parseDate(date), {
        start: parseDate(dueDateFrom),
        end: parseDate(dueDateTo),
      });

      if (isDateInRange) {
        setSelectedDate(getApiDateString(date));
        return;
      }
    }

    onFilterChange(date);
  };

  return {
    rentalOrders,
    isError: isGridQueryErrored || isGridErrored,
    selectedDate: parsedSelectedDate,
    onNavigate,
  };
}

/** @constant {string} SELECTED_DATE - Query parameter key for storing current calendar date */
const SELECTED_DATE = 'selectedDate';

/** @constant {string} DUE_DATE_FROM - Query parameter key for storing due date from */
const DUE_DATE_FROM = 'dueDateFrom';

/** @constant {string} DUE_DATE_TO - Query parameter key for storing due date to */
const DUE_DATE_TO = 'dueDateTo';

/**
 * @constant {number} TASK_POLLING_INTERVAL - Polling interval for task updates in milliseconds
 */
const TASK_POLLING_INTERVAL = 15 * 60 * 1000;
